import { useEffect } from 'react';
import { useShopifyCart } from './useShopifyCart';
import { toast } from 'sonner';
import { productionLogger } from '@/utils';

/**
 * Detects Shopify checkout completion and clears cart
 * 
 * Shopify doesn't provide webhooks for headless stores, so we use:
 * 1. URL parameter detection (when returning from Shopify)
 * 2. localStorage timestamp tracking (to prevent false positives)
 * 3. Graceful cart clearing with user notification
 */
export const useCheckoutSuccess = () => {
  const { clearCart, getTotalItems } = useShopifyCart();

  useEffect(() => {
    // Only run on client-side
    if (typeof window === 'undefined') return;

    const checkForCheckoutSuccess = () => {
      const params = new URLSearchParams(window.location.search);
      const checkoutSuccess = params.get('checkout_success');
      const checkoutId = params.get('checkout_id');
      
      // Check if we're returning from a successful checkout
      if (checkoutSuccess === 'true' && checkoutId) {
        const lastCheckoutId = localStorage.getItem('last_processed_checkout_id');
        
        // Prevent duplicate processing of the same checkout
        if (lastCheckoutId === checkoutId) {
          productionLogger.info('Checkout already processed:', checkoutId);
          return;
        }

        // Clear the cart
        const itemCount = getTotalItems();
        if (itemCount > 0) {
          clearCart();
          toast.success("Order completed!", {
            description: `Thank you for your purchase! Your order has been confirmed.`,
          });
          productionLogger.info('Cart cleared after checkout:', checkoutId);
        }

        // Store this checkout ID to prevent reprocessing
        localStorage.setItem('last_processed_checkout_id', checkoutId);
        
        // Clean up URL parameters
        const newUrl = window.location.pathname;
        window.history.replaceState({}, '', newUrl);
      }
    };

    checkForCheckoutSuccess();
  }, [clearCart, getTotalItems]);
};
